import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, ZoomControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Search, X } from "lucide-react";

/* One tile source for both themes: OSM's standard light tiles. Dark·Gold
   doesn't swap providers — index.css inverts the tile pane under
   [data-theme="dark"] so the map darkens without a second basemap. We used to
   pull CARTO's dark_all here, but CARTO now stamps "API KEY REQUIRED" into
   keyless tiles (HTTP 200, watermark burned into the PNG — nothing to catch). */
const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

// One pin-worthy listing as returned by GET /business-listings/map-points.
export interface ListingMapPoint {
  id: string;
  title: string;
  asking_price: number | null;
  latitude: number | null;
  longitude: number | null;
  listing_image_url: string | null;
  city_name: string | null;
  state_name: string | null;
  country_name: string | null;
  category_name?: string | null;
}

// Fallback coordinates for listings the geocoder hasn't resolved yet — pin
// them at their US state's centroid so every listing still shows on the map.
const STATE_CENTROIDS: Record<string, [number, number]> = {
  Alabama: [32.806671, -86.79113], Alaska: [61.370716, -152.404419], Arizona: [33.729759, -111.431221],
  Arkansas: [34.969704, -92.373123], California: [36.116203, -119.681564], Colorado: [39.059811, -105.311104],
  Connecticut: [41.597782, -72.755371], Delaware: [39.318523, -75.507141], "District of Columbia": [38.897438, -77.026817],
  Florida: [27.766279, -81.686783], Georgia: [33.040619, -83.643074], Hawaii: [21.094318, -157.498337],
  Idaho: [44.240459, -114.478828], Illinois: [40.349457, -88.986137], Indiana: [39.849426, -86.258278],
  Iowa: [42.011539, -93.210526], Kansas: [38.5266, -96.726486], Kentucky: [37.66814, -84.670067],
  Louisiana: [31.169546, -91.867805], Maine: [44.693947, -69.381927], Maryland: [39.063946, -76.802101],
  Massachusetts: [42.230171, -71.530106], Michigan: [43.326618, -84.536095], Minnesota: [45.694454, -93.900192],
  Mississippi: [32.741646, -89.678696], Missouri: [38.456085, -92.288368], Montana: [46.921925, -110.454353],
  Nebraska: [41.12537, -98.268082], Nevada: [38.313515, -117.055374], "New Hampshire": [43.452492, -71.563896],
  "New Jersey": [40.298904, -74.521011], "New Mexico": [34.840515, -106.248482], "New York": [42.165726, -74.948051],
  "North Carolina": [35.630066, -79.806419], "North Dakota": [47.528912, -99.784012], Ohio: [40.388783, -82.764915],
  Oklahoma: [35.565342, -96.928917], Oregon: [44.572021, -122.070938], Pennsylvania: [40.590752, -77.209755],
  "Rhode Island": [41.680893, -71.51178], "South Carolina": [33.856892, -80.945007], "South Dakota": [44.299782, -99.438828],
  Tennessee: [35.747845, -86.692345], Texas: [31.054487, -97.563461], Utah: [40.150032, -111.862434],
  Vermont: [44.045876, -72.710686], Virginia: [37.769337, -78.169968], Washington: [47.400902, -121.490494],
  "West Virginia": [38.491226, -80.954453], Wisconsin: [44.268543, -89.616508], Wyoming: [42.755966, -107.30249],
  "Puerto Rico": [18.220833, -66.590149],
};

const fmtPrice = (n: number | null): string => {
  if (n == null) return "Price on request";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}m`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return `$${n}`;
};

interface PinGroup {
  lat: number;
  lng: number;
  label: string;
  items: ListingMapPoint[];
}

// Circular count bubble in the brand gold — avoids Leaflet's default icon
// asset issues under Vite and reads better for grouped city pins.
// `highlighted` pins (matching the current location search) render GREEN so a
// "Texas" search makes every Texas pin pop against the rest of the map.
const groupIcon = (count: number, highlighted = false) => {
  const size = count > 1 ? Math.min(46, 30 + String(count).length * 5) : 26;
  const bg = highlighted ? "#059669" : "var(--color-primary)";
  const fg = highlighted ? "#ffffff" : "var(--color-primary-foreground)";
  const shadow = highlighted ? "0 0 0 4px rgba(16,185,129,.35), 0 2px 8px rgba(6,60,40,.5)" : "0 2px 8px rgba(40,30,10,.45)";
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;background:${bg};color:${fg};border:2.5px solid #fff;box-shadow:${shadow};display:flex;align-items:center;justify-content:center;font:700 ${count > 1 ? 13 : 11}px/1 system-ui,sans-serif;">${count > 1 ? count : "●"}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Location filter set by the search box — typed so state searches highlight by
// state membership and city searches by exact pin label.
type LocationFilter = { type: "state" | "city"; label: string };

const groupMatchesFilter = (g: PinGroup, filter: LocationFilter): boolean =>
  filter.type === "state"
    ? g.items.some((p) => p.state_name === filter.label)
    : g.label.toLowerCase() === filter.label.toLowerCase();

// Default view: the continental United States. Fitting the pins' bounds instead
// would zoom way out whenever Alaska / Hawaii / Puerto Rico listings exist —
// those stay reachable by panning, but the lower 48 is what opens on screen.
const US_BOUNDS = L.latLngBounds([24.4, -125.0], [49.6, -66.9]);

// Applies search focus (and the initial continental-US fit) to the Leaflet map.
function MapController({
  focus,
}: {
  focus: { bounds: L.LatLngBounds; ts: number } | null;
}) {
  const map = useMap();
  const didInitRef = useRef(false);

  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true;
      map.fitBounds(US_BOUNDS, { padding: [10, 10] });
    }
  }, [map]);

  useEffect(() => {
    if (focus) map.fitBounds(focus.bounds, { padding: [40, 40], maxZoom: 10 });
  }, [focus, map]);

  return null;
}

export function ListingsMap({
  points,
  onView,
  resetToken,
}: {
  points: ListingMapPoint[];
  onView: (listingId: string) => void;
  /** Change this to clear search/highlight state and refit the US frame.
      Lets callers swap pin sets (Active ↔ Coming Soon) without remounting
      the whole map — a remount re-downloads every visible tile. */
  resetToken?: string | number;
}) {
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focus, setFocus] = useState<{ bounds: L.LatLngBounds; ts: number } | null>(null);
  const [activeFilter, setActiveFilter] = useState<LocationFilter | null>(null);

  // Pin-set swap: wipe the previous set's search/highlight state and return to
  // the default continental-US frame, keeping the Leaflet instance (and its
  // already-loaded tiles) alive. Skips the initial mount — MapController
  // handles the first fit.
  const skipFirstResetRef = useRef(true);
  useEffect(() => {
    if (skipFirstResetRef.current) {
      skipFirstResetRef.current = false;
      return;
    }
    setQuery("");
    setActiveFilter(null);
    setShowSuggestions(false);
    setFocus({ bounds: US_BOUNDS, ts: Date.now() });
  }, [resetToken]);

  // Group listings by resolved coordinate so co-located businesses become one
  // numbered pin (all listings in a city share the same geocoded point).
  const groups = useMemo<PinGroup[]>(() => {
    const byCoord = new Map<string, PinGroup>();
    for (const p of points) {
      let lat = p.latitude;
      let lng = p.longitude;
      if (lat == null || lng == null) {
        const centroid = p.state_name ? STATE_CENTROIDS[p.state_name] : undefined;
        if (!centroid) continue; // no way to place it
        [lat, lng] = centroid;
      }
      const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
      // Centroid-fallback pins hold everything in the state, so label them with
      // the state alone — not whichever city happened to arrive first.
      const usedCentroid = p.latitude == null || p.longitude == null;
      const label = usedCentroid
        ? p.state_name ?? ""
        : [p.city_name, p.state_name].filter(Boolean).join(", ") || p.state_name || "";
      const existing = byCoord.get(key);
      if (existing) existing.items.push(p);
      else byCoord.set(key, { lat, lng, label, items: [p] });
    }
    return [...byCoord.values()];
  }, [points]);

  // Searchable locations: states first, then cities, both with listing counts.
  const locations = useMemo(() => {
    const states = new Map<string, number>();
    const cities = new Map<string, { label: string; count: number }>();
    for (const p of points) {
      if (p.state_name) states.set(p.state_name, (states.get(p.state_name) ?? 0) + 1);
      if (p.city_name && p.state_name) {
        const label = `${p.city_name}, ${p.state_name}`;
        const cur = cities.get(label.toLowerCase());
        cities.set(label.toLowerCase(), { label, count: (cur?.count ?? 0) + 1 });
      }
    }
    return [
      ...[...states.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ type: "state" as const, label: name, count })),
      ...[...cities.values()]
        .sort((a, b) => b.count - a.count)
        .map(({ label, count }) => ({ type: "city" as const, label, count })),
    ];
  }, [points]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return locations.filter((l) => l.label.toLowerCase().includes(q)).slice(0, 8);
  }, [locations, query]);

  const focusLocation = (loc: LocationFilter) => {
    const matches = groups.filter((g) => groupMatchesFilter(g, loc));
    if (!matches.length) return;
    setFocus({
      bounds: L.latLngBounds(matches.map((g) => [g.lat, g.lng] as [number, number])),
      ts: Date.now(),
    });
    setActiveFilter(loc);
    setQuery(loc.label);
    setShowSuggestions(false);
  };

  const clearSearch = () => {
    setQuery("");
    setActiveFilter(null);
    setShowSuggestions(false);
    // Back to the default continental-US frame.
    setFocus({ bounds: US_BOUNDS, ts: Date.now() });
  };

  return (
    // relative z-0 keeps Leaflet's internal z-indexes from floating above the
    // app's sticky header and modals.
    <div className="relative z-0 h-full w-full overflow-hidden rounded-[1.1rem]">
      {/* Default zoom control is top-left, under the search overlay — render
          it bottom-right instead. */}
      <MapContainer
        center={[39.5, -98.35]}
        zoom={4}
        minZoom={3}
        scrollWheelZoom
        zoomControl={false}
        attributionControl={false}
        className="h-full w-full"
      >
        <ZoomControl position="bottomright" />
        <TileLayer url={TILE_URL} />
        <MapController focus={focus} />
        {groups.map((g) => {
          // Green pin when this group matches the searched state/city.
          const highlighted = activeFilter ? groupMatchesFilter(g, activeFilter) : false;
          return (
          <Marker key={`${g.lat},${g.lng},${highlighted}`} position={[g.lat, g.lng]} icon={groupIcon(g.items.length, highlighted)}>
            <Popup>
              <div className="min-w-[200px] max-w-[240px]">
                <div className="flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-gray-400">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {g.label}
                  {g.items.length > 1 && <span>· {g.items.length} listings</span>}
                </div>
                <div className="mt-1.5 max-h-52 space-y-1.5 overflow-y-auto pr-1">
                  {g.items.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => onView(p.id)}
                      className="block w-full rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2 text-left transition-colors hover:border-primary/30 hover:bg-white"
                    >
                      <span className="block text-xs font-bold leading-snug text-gray-900 line-clamp-2">{p.title}</span>
                      <span className="mt-0.5 block text-[0.7rem] font-semibold text-emerald-700">{fmtPrice(p.asking_price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>

      {/* Search overlay — type a state or city (e.g. "California") to fly there */}
      <div className="absolute left-3 right-14 top-3 z-[1200] sm:right-auto sm:w-64">
        <div className="flex items-center gap-2 rounded-xl border border-white/60 bg-white/95 px-3 py-2 shadow-md backdrop-blur-sm">
          <Search className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
              if (!e.target.value.trim()) setActiveFilter(null);
            }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && suggestions.length) focusLocation(suggestions[0]);
              if (e.key === "Escape") setShowSuggestions(false);
            }}
            placeholder="Search state or city…"
            className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
          {query && (
            <button onClick={clearSearch} title="Clear search" className="text-gray-400 transition-colors hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {showSuggestions && query.trim() && (
          <div className="mt-1.5 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
            {suggestions.length ? (
              suggestions.map((s) => (
                <button
                  key={`${s.type}:${s.label}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    focusLocation(s);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                >
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="flex-1 truncate">{s.label}</span>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] font-bold text-primary">{s.count}</span>
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-sm text-gray-400">No listings match that location</p>
            )}
          </div>
        )}
      </div>

      {/* Pin count chip */}
      <div className="absolute bottom-3 left-3 z-[1200] rounded-full border border-white/60 bg-white/95 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-md backdrop-blur-sm">
        {activeFilter
          ? `${groups.filter((g) => groupMatchesFilter(g, activeFilter)).reduce((n, g) => n + g.items.length, 0).toLocaleString()} in ${activeFilter.label}`
          : `${points.length.toLocaleString()} businesses on the map`}
      </div>
    </div>
  );
}
